// External imports
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import React from 'react';

// Internal imports
import { CircularProgress } from './CircularProgress';

// Declare module augmentation for ButtonPropsColorOverrides
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    alert: true;
  }
}

interface CustomButtonProps {
  loading?: boolean;
}

export function Button<C extends React.ElementType = 'button'>(
  props: MuiButtonProps<C, { component?: C } & CustomButtonProps>
) {
  const {
    variant = 'text',
    color = 'primary',
    sx,
    loading,
    children,
    ...otherProps
  } = props;
  const isFilled = variant === 'contained';
  const isAlert = color === 'alert';

  const styles = [
    { transition: 'all 0.3s' },
    isFilled && {
      bgcolor: `${color}.solid`,
      color: `${color}.contrastText`,
      backgroundImage: 'none',
      boxShadow: 'none',
      border: 'none',
      '&:hover': {
        bgcolor: `${color}.solidHover`,
        outlineColor: 'transparent',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
    },
    variant === 'outlined' && {
      boxShadow: 'none',
      color: `${color}.text`,
      borderColor: `${color}.borderStrong`,
      '&:hover': {
        borderColor: `${color}.borderStrong`,
      },
    },
    variant === 'text' && {
      color: `${color}.text`,
    },
  ];

  return (
    <MuiButton
      sx={[...styles, ...(Array.isArray(sx) ? sx : [sx])]}
      variant={variant}
      color={isAlert ? 'alert' : color}
      {...otherProps}
    >
      {loading ? (
        <CircularProgress color="inherit" size="small" sx={{ p: '3px' }} />
      ) : (
        children
      )}
    </MuiButton>
  );
}

type ButtonProps = MuiButtonProps & CustomButtonProps;

export type { ButtonProps };
